import _ from "lodash";
import {
  formatOrderStatusLabel,
  getOrderStatusBackground,
  normalizeOrderStatus,
} from "./orderListTable";

/** Sub-status labels while order workflow step stays on Approved / Paid / Packed. */
export const PAYMENT_SUB_STATUS_LABELS = {
  partial_paid: "Partial Paid",
  unpaid: "Unpaid",
  reject_shipment: "Reject Shipment",
  unpacked: "Unpacked",
};

const DISPLAY_STATUS_COLORS = {
  partial_paid: { background: "#7B61FF" },
  unpaid: { background: "#FF3850" },
  reject_shipment: { background: "#CC2200" },
  unpacked: { background: "#9C9C9C" },
};

export function normalizePaymentStatus(paymentStatus) {
  const key = String(paymentStatus ?? "").trim().toLowerCase();
  if (key === "not_paid") return "unpaid";
  return key;
}

/** Only these values appear on the "Payment" badge — not packing/shipment sub-statuses. */
const PAYMENT_BADGE_STATUSES = new Set([
  "pending",
  "paid",
  "unpaid",
  "partial_paid",
]);

export function isPaymentBadgeStatus(paymentStatus) {
  return PAYMENT_BADGE_STATUSES.has(normalizePaymentStatus(paymentStatus));
}

/** Payment badge value for order detail header (Payment: Paid / Pending / etc.). */
export function resolvePaymentBadgeStatus(order) {
  const orderStatus = normalizeOrderStatus(order?.order_status);
  const paymentStatus = normalizePaymentStatus(order?.payment_status);

  if (orderStatus === "approved") {
    if (isPaymentBadgeStatus(paymentStatus) && paymentStatus !== "paid") {
      return paymentStatus || "pending";
    }
    return "pending";
  }

  if (orderStatus === "bill_generated") {
    if (isPaymentBadgeStatus(paymentStatus) && paymentStatus !== "paid") {
      return paymentStatus || "pending";
    }
    return "pending";
  }

  const postPaymentStages = new Set([
    "paid",
    "packing",
    "recieved",
    "shipment",
    "wait_to_deliver",
    "un_delivered",
    "delivered",
  ]);
  if (postPaymentStages.has(orderStatus)) {
    return "paid";
  }

  if (isPaymentBadgeStatus(paymentStatus)) {
    return paymentStatus;
  }

  return "pending";
}

/**
 * Dynamic status label for tables and order detail headline.
 * Workflow step (order_status) unchanged; payment sub-status shown when set.
 */
export function resolveOrderDisplayStatus(order) {
  const orderStatus = normalizeOrderStatus(order?.order_status);
  const paymentStatus = normalizePaymentStatus(order?.payment_status);

  if ((orderStatus === "approved" || orderStatus === "bill_generated") && PAYMENT_SUB_STATUS_LABELS[paymentStatus]) {
    return {
      key: paymentStatus,
      label: PAYMENT_SUB_STATUS_LABELS[paymentStatus],
    };
  }

  if (orderStatus === "paid" && paymentStatus === "unpacked") {
    return { key: "unpacked", label: PAYMENT_SUB_STATUS_LABELS.unpacked };
  }

  if (orderStatus === "paid" && paymentStatus === "reject_shipment") {
    return {
      key: "reject_shipment",
      label: PAYMENT_SUB_STATUS_LABELS.reject_shipment,
    };
  }

  if (orderStatus === "packing" && paymentStatus === "reject_shipment") {
    return {
      key: "reject_shipment",
      label: PAYMENT_SUB_STATUS_LABELS.reject_shipment,
    };
  }

  return {
    key: orderStatus,
    label: formatOrderStatusLabel(orderStatus),
  };
}

export function getOrderDisplayStatusBackground(order) {
  const { key } = resolveOrderDisplayStatus(order);
  return (
    DISPLAY_STATUS_COLORS[key]?.background ??
    getOrderStatusBackground(key)
  );
}

export function formatOrderDisplayStatusLabel(order) {
  return resolveOrderDisplayStatus(order).label;
}

export function getPaymentSubStatusHeadline(order) {
  const orderStatus = normalizeOrderStatus(order?.order_status);
  const paymentStatus = normalizePaymentStatus(order?.payment_status);
  const label = PAYMENT_SUB_STATUS_LABELS[paymentStatus];

  if ((orderStatus === "approved" || orderStatus === "bill_generated") && label) {
    return {
      title: orderStatus === "bill_generated" ? "Bill Generated" : "Approved",
      sub: `Payment status: ${label}.`,
    };
  }

  if (orderStatus === "paid" && paymentStatus === "unpacked") {
    return {
      title: "Unpacked",
      sub: "Items unpacked. Ready to pack again when needed.",
    };
  }

  if (orderStatus === "paid" && paymentStatus === "reject_shipment") {
    return {
      title: "Paid",
      sub: "Shipment rejected.",
    };
  }

  if (orderStatus === "packing" && paymentStatus === "reject_shipment") {
    return {
      title: "Packed",
      sub: "Shipment rejected.",
    };
  }

  return null;
}

export function formatPaymentStatusLabel(paymentStatus) {
  const key = normalizePaymentStatus(paymentStatus);
  if (key === "paid") return "Paid";
  if (key === "pending") return "Pending";
  if (PAYMENT_SUB_STATUS_LABELS[key]) {
    return PAYMENT_SUB_STATUS_LABELS[key];
  }
  if (!key) return "Pending";
  return _.startCase(key.replace(/_/g, " "));
}

/** Dynamic stepper labels for Paid / Packed / Shipped (Created & Approved unchanged). */
export function resolveTrackingStepLabels(order) {
  if (!order) return {};

  const orderStatus = normalizeOrderStatus(order?.order_status);
  const paymentStatus = normalizePaymentStatus(order?.payment_status);
  const overrides = {};

  if ((orderStatus === "approved" || orderStatus === "bill_generated") && PAYMENT_SUB_STATUS_LABELS[paymentStatus]) {
    overrides.bill_generated = PAYMENT_SUB_STATUS_LABELS[paymentStatus];
  }

  if (orderStatus === "paid" && paymentStatus === "unpacked") {
    overrides.packing = PAYMENT_SUB_STATUS_LABELS.unpacked;
  }

  if (
    orderStatus === "paid" &&
    paymentStatus === "reject_shipment"
  ) {
    overrides.packing = PAYMENT_SUB_STATUS_LABELS.reject_shipment;
  }

  if (orderStatus === "packing" && paymentStatus === "reject_shipment") {
    overrides.shipment = PAYMENT_SUB_STATUS_LABELS.reject_shipment;
  }

  return overrides;
}
